import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useLocation } from 'react-router-dom';
import CallWidget from '../../../components/common/CallWidget/CallWidget';
import LeadDetails from './LeadDetails';
import UniversalDetailContent from '../../../components/common/Panels/UniversalDetailContent';
import LeadList from './components/LeadList';
import LeadListWrapper from './components/LeadListWrapper';
import LeadHeader from './components/LeadHeader';
import { Filter } from 'lucide-react';
import FilterPanel from './components/FilterPanel.jsx';
import LeadCustomField from './components/LeadCustomField';
import { getLeadList, bindSourceMediumCampaign, getFollowUpStatus, getUserListByUserId, getCustomFieldByParentId, getLeadCustomSearch, getAllPermissionByModuleCodeUserId, checkLeadPermission, getFieldMasked } from '../../../utils/lead';
import { getSession } from '../../../getSession';
import { serviceInstance, wcfInstance } from '../../../axiosinstance';
import nodata from '../../../assets/nodata.gif';
import PremiumModuleCards from '../../Task/Components/common/PremiumModuleCards';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import dayjs from 'dayjs';
import { MassOperationDrawer } from '../../../components/common/MassOperation/MassOperation';
import SendSMS from '../../../components/LeadMass/SendSMS';
import SendMail from '../../../components/LeadMass/SendMail';
import VoiceBroadCast from '../../../components/LeadMass/VoiceBroadCast';
import UpdateForm from '../../../components/LeadMass/UpdateForm';
import AddTaskForm from '../../../components/LeadMass/AddTaskForm';
import AddAppointmentForm from '../../../components/LeadMass/AddAppointmentForm';
import MarkettingSequence from '../../../components/LeadMass/MarkettingSequence';
import SendInternationalSMS from '../../../components/LeadMass/SendInternationalSMS';
import AddRemoveTag from '../../../components/LeadMass/AddRemoveTag';
import AddCallList from '../../../components/LeadMass/AddCallList';
import AddFollowupMassOperation from '../../../components/LeadMass/AddFollowupMassOperation';
import AddFollowupForm from '../../Enquiries/Forms/AddFollowupForm';
import EditLeadForm from '../../../components/LeadForm/EditLeadForm';
import PopUpModal from '../../../components/common/Modal';
import Button from '../../../components/common/Button';
import AddLeadForm from '../../../components/LeadForm/AddLeadForm';
import { BsTelephonePlus } from "react-icons/bs";
import Swal from 'sweetalert2';
import { getInitials, getAvatarColorScheme } from '../../../utils/helpers';
import ScheduleCallForm from './components/ScheduleCallForm';
import Drawer from '../../../components/common/Drawer';
import Loader from '../../../components/Loader/Loader';
import { PermissionDeniedComponent } from '../../../hooks/useModulePermissions';
import './Leads.css';
import ImportData from '../../../components/ImportData/ImportData';



const Leads = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const incomingSearchName = searchParams.get('searchName') || '';
  const incomingFilter = searchParams.get('filter') || '';

  const [selectedLead, setSelectedLead] = useState(null);
  const [totalRecord, setTotalRecord] = useState(0);
  const [leads, setLeads] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState('20');
  const { userId, TokenId, parentId } = getSession();
  const [searchText, setSearchText] = useState('');
  const [customSearchName, setCustomSearchName] = useState(incomingSearchName);
  const [leadFilterText, setLeadFilterText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (incomingSearchName !== customSearchName) {
      setCustomSearchName(incomingSearchName);
      setCurrentPage(1);
    }
  }, [incomingSearchName]);

  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowSelection, setRowSelection] = useState({});
  const [isAllSelectedAcrossPages, setIsAllSelectedAcrossPages] = useState(false);
  const [activeFilters, setActiveFilters] = useState(incomingFilter ? [incomingFilter] : ['All']);

  const [isExporting, setIsExporting] = useState(false);
  const [isImportDataModal, setIsImportDataModal] = useState(false);
  const [showCustomFields, setShowCustomFields] = useState(false);
  const [filterBtnLeft, setFilterBtnLeft] = useState(72);

  // ── Role Permissions States and Fetching ─────────────────────────────────
  const [permissions, setPermissions] = useState(null);
  const [leadPermissions, setLeadPermissions] = useState(null);
  const [isLeadPermLoading, setIsLeadPermLoading] = useState(false);
  const [maskedFields, setMaskedFields] = useState([]);
  const hasFetchedMaskedFields = useRef(false);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const perm = await getAllPermissionByModuleCodeUserId({
          Mode: 'S',
          ModuleCode: 'PAG10031',
          UserId: userId,
          Token: TokenId
        });
        setPermissions(perm);
      } catch (err) {
        console.error('Lead page permission fetch error:', err);
      }
    };
    if (userId) fetchPermissions();
  }, [userId, TokenId]);

  useEffect(() => {
    const fetchLeadPerm = async () => {
      if (!selectedLead) {
        setLeadPermissions(null);
        return;
      }
      const leadId = selectedLead.ID || selectedLead.LeadId || selectedLead.Id;
      if (!leadId) return;

      setIsLeadPermLoading(true);
      try {
        const result = await checkLeadPermission({
          leadId,
          logUserId: userId,
          Token: TokenId
        });
        const permRow = result?.Details && Array.isArray(result.Details) && result.Details.length > 0
          ? result.Details[0]
          : (result?.Details || null);
        setLeadPermissions(permRow);
      } catch (err) {
        console.error('checkLeadPermission error:', err);
        setLeadPermissions(null);
      } finally {
        setIsLeadPermLoading(false);
      }
    };

    fetchLeadPerm();
  }, [selectedLead, userId, TokenId]);

  useEffect(() => {
    const fetchMaskedFields = async () => {
      if (hasFetchedMaskedFields.current || !selectedLead) return;
      try {
        const res = await getFieldMasked({
          ParentId: parentId,
          UserId: userId,
          PageCode: 'PAG10031'
        });
        if (res && res.d) {
          const list = Array.isArray(res.d) ? res.d : (res.d?.d || []);
          setMaskedFields(list);
          hasFetchedMaskedFields.current = true;
        }
      } catch (err) {
        console.error('fetchMaskedFields error:', err);
      }
    };
    fetchMaskedFields();
  }, [selectedLead, userId, parentId]);

  const canAdd = permissions ? permissions.Add !== false : true;
  const canEdit = permissions ? permissions.Edit !== false : true;
  const canDelete = permissions ? permissions.Delete !== false : true;
  const canView = permissions ? permissions.View !== false : true;


  const handleExportLeadList = async () => {
    try {
      setIsExporting(true);

      Swal.fire({
        title: 'Exporting',
        text: 'Please wait while your leads are being exported...',
        icon: 'info',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const selectedIds = Object.keys(rowSelection).map(id => String(id).trim()).filter(Boolean);
      const hasSelectedRecords = selectedIds.length > 0;
      const totalRecordsToExport = hasSelectedRecords ? selectedIds.length : Number(totalRecord || 0);

      let finalFilter = customSearchName ? '' : (leadFilterText || '');
      if (searchText && !customSearchName) {
        const s = searchText.trim();
        const searchSql = ` (( PersonName like '${s}%') or (MobileNo1 like '${s}%') or (MobileNo2 like '${s}%') or (MobileNo3 like '${s}%') or (EmailId like '${s}%') or (EmailId1 like '${s}%') or (EmailId2 like '${s}%') )`;
        finalFilter = finalFilter ? `(${finalFilter}) AND ${searchSql}` : searchSql;
      }

      const exportData = {
        UserId: String(userId || ''),
        ParentId: String(parentId || ''),
        SearchName: String(customSearchName || ''),
        FilterText: String(finalFilter || ''),
        OrderStr: '',
        NotFollowUp: 0,
        TextSearch: '',
        startPoint: 1,
        endPoint: totalRecordsToExport
      };

      if (hasSelectedRecords) {
        exportData.selectedIds = selectedIds;
      }

      const response = await wcfInstance.post(
        'UserCRMCampaign/Service/LeadService.asmx/ExportLeadList',
        exportData,
        {
          responseType: 'blob',
          validateStatus: () => true
        }
      );

      Swal.close();

      const statusCode = response?.status;
      const contentType = String(response?.headers?.['content-type'] || '').toLowerCase();
      const blobData = response?.data;

      // Helper to parse blob as JSON
      const parseBlobJson = async (blob) => {
        try {
          const text = await blob.text();
          return JSON.parse(text);
        } catch (e) {
          return null;
        }
      };

      if (statusCode === 202 || contentType.includes('application/json')) {
        const parsed = await parseBlobJson(blobData);
        const infoMsg = parsed?.batchId
          ? `${parsed?.message || 'Export request logged for background processing.'} Batch ID: ${parsed.batchId}`
          : (parsed?.message || 'Export request has been logged for background processing. You will receive notification when export is ready.');
        Swal.fire('Info', infoMsg, 'info');
        return;
      }

      if (statusCode === 200 && (contentType.includes('excel') || contentType.includes('spreadsheet') || contentType.includes('octet-stream') || contentType.includes('vnd.ms-excel'))) {
        const blob = new Blob([blobData], { type: 'application/vnd.ms-excel' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `LeadExport_${new Date().getTime()}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        Swal.fire('Success', 'Export downloaded successfully.', 'success');
        return;
      }

      if (statusCode >= 200 && statusCode < 300) {
        Swal.fire('Success', 'Export completed successfully.', 'success');
      } else {
        Swal.fire('Error', 'An error occurred during export. Please try again.', 'error');
      }
    } catch (error) {
      if (Swal.isVisible()) Swal.close();
      console.error('Export error:', error);
      Swal.fire('Error', 'Error exporting Lead Details, please try again or contact support.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const [globalStats, setGlobalStats] = useState({
    total: 0,
    fresh: 0,
    today: 0,
    followedUp: 0,
    converted: 0,
    absoluteTotal: 0
  });

  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Modal States
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [selectedLeadForEdit, setSelectedLeadForEdit] = useState(null);

  // Mass Operation Modal States
  const [isMassAddFollowupModal, setIsMassAddFollowupModal] = useState(false);
  const [isMassSendSMS, setIsMassSendSMS] = useState(false);
  const [isMassSendMail, setIsMassSendMail] = useState(false);
  const [isMassSendVoiceBroadCast, setIsMassSendVoiceBroadCast] = useState(false);
  const [isMassUpdateLead, setIsMassUpdateLead] = useState(false);
  const [isMassAddTask, setIsMassAddTask] = useState(false);
  const [isMassAddAppointment, setIsMassAddAppointment] = useState(false);
  const [isMassMarketingSequence, setIsMassMarketingSequence] = useState(false);
  const [isSendInternationalSMS, setIsSendInternationalSMS] = useState(false);
  const [isMassAddTag, setIsMassAddTag] = useState(false);
  const [isAddTag, setIsAddTag] = useState(true);
  const [isMassAddCallList, setIsMassAddCallList] = useState(false);
  const [showMassDeleteConfirm, setShowMassDeleteConfirm] = useState(false);

  // Submit Handler States
  const [massSmsSubmitHandler, setMassSmsSubmitHandler] = useState(null);
  const [massMailSubmitHandler, setMassMailSubmitHandler] = useState(null);
  const [massVoiceSubmitHandler, setMassVoiceSubmitHandler] = useState(null);
  const [massUpdateSubmitHandler, setMassUpdateSubmitHandler] = useState(null);
  const [massTaskSubmitHandler, setMassTaskSubmitHandler] = useState(null);
  const [massAppointmentSubmitHandler, setMassAppointmentSubmitHandler] = useState(null);
  const [massSequenceSubmitHandler, setMassSequenceSubmitHandler] = useState(null);
  const [massIntlSmsSubmitHandler, setMassIntlSmsSubmitHandler] = useState(null);
  const [massTagSubmitHandler, setMassTagSubmitHandler] = useState(null);
  const [massCallListSubmitHandler, setMassCallListSubmitHandler] = useState(null);

  // Call Widget States
  const [showCallWidget, setShowCallWidget] = useState(false);
  const [callWidgetData, setCallWidgetData] = useState(null);

  const handleInitiateCall = (lead, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setCallWidgetData({
      toMobileNo: lead.MobileNo || lead.MobileNumber || lead.CsvMobileNo || '',
      entityId: lead.ID || lead.LeadId || lead.Id,
      entityName: 'lead',
      personName: lead.PersonName || 'Customer',
      triggerRect: rect,
      toEmail: lead.EmailID || lead.EmailId || '',
      extraContactInfo: null
    });
    setShowCallWidget(true);
  };

  // Schedule Call States
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleLead, setScheduleLead] = useState(null);

  const leadFieldDefs = useMemo(() => [
    { key: 'personName', label: 'Name', db: 'PersonName' },
    { key: 'companyName', label: 'Company', db: 'CompanyName' },
    { key: 'leadNo', label: 'Lead No', db: 'LeadNo' },
    { key: 'mobileNo', label: 'Mobile', db: '' },
    { key: 'emailId', label: 'Email', db: '' },
    { key: 'sourceName', label: 'Source', db: 'SourceName' },
    { key: 'mediumName', label: 'Medium', db: 'MediumName' },
    { key: 'campaignName', label: 'Campaign', db: 'CampaignName' },
    { key: 'followUpStatus', label: 'Followup Status', db: 'FollowUpStatus' },
    { key: 'assignedTo', label: 'Assigned To', db: 'AssignedTo' },
    { key: 'createdDate', label: 'Created Date', db: 'CreatedDate' },
    { key: 'city', label: 'City', db: 'City' },
    { key: 'state', label: 'State', db: 'State' },
    { key: 'country', label: 'Country', db: 'Country' }
  ], []);

  const [sourceOptions, setSourceOptions] = useState([]);
  const [mediumOptions, setMediumOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [followupOptions, setFollowupOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const makeInitialFilters = () => {
    return leadFieldDefs.reduce((acc, f) => {
      acc[f.key] = { enabled: false, operator: 'Contains', value: '' };
      return acc;
    }, {});
  };

  const [advancedFilters, setAdvancedFilters] = useState(() => makeInitialFilters());
  const [customFields, setCustomFields] = useState([]);

  const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
  const Lead_for_Redirect = `${WCF_BASE}${API_ENDPOINTS.LEADS.Lead_For_Redirect}`;
  const fetchStats = async () => {
    try {
      const ustrUserId = userId || localStorage.getItem('userId') || '34594';

      const payload = {
        Details: {
          UserId: ustrUserId,
          strLeadSection: "leads",
          SearchText: "",
          FullText: "",
          dashboardFilter: ""
        },
        Message: "",
        Status: "",
        Token: String(API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN)
      };

      const response = await serviceInstance.post(API_ENDPOINTS.DASHBOARD.GET_LEAD_DETAILS, payload);
      if (response.data && response.data.Details && response.data.Details.LeadMatrix_CLS_List) {
        const metrics = response.data.Details.LeadMatrix_CLS_List[0];
        setGlobalStats(prev => ({
          ...prev,
          total: parseInt(metrics.Total) || prev.total || 0,
          fresh: parseInt(metrics.Fresh) || 0,
          today: prev.today || parseInt(metrics.Today || metrics.Fresh) || 0,
          followedUp: parseInt(metrics.FollowedUp) || 0,
          converted: parseInt(metrics.Converted) || 0,
          absoluteTotal: parseInt(metrics.Total) || prev.absoluteTotal || 0
        }));
      }
    } catch (error) {
      console.warn("Error fetching lead stats:", error);
    }

    // Additional call for today's lead count as requested
    try {
      const yesterday = dayjs().subtract(1, 'day').format('DD-MMM-YYYY');
      const tomorrow = dayjs().add(1, 'day').format('DD-MMM-YYYY');
      const todayFilter = `CreatedOn1 >= '${yesterday}' AND CreatedOn1 <= '${tomorrow}'`;

      const payload = {
        Token: TokenId,
        Details: JSON.stringify({
          draw: 1,
          StartNo: 0,
          EndNo: 1,
          UserId: userId || '',
          ParentId: parentId || '',
          FilterText: todayFilter
        })
      };
      const response = await serviceInstance.post('UserCRM/GetLeadDetailListNew', payload);
      if (response?.data?.Status === 1 && response.data.Details) {
        setGlobalStats(prev => ({
          ...prev,
          today: response.data.Details.recordsTotal || 0
        }));
      }
    } catch (error) {
      console.warn('fetchTodayCount error:', error);
    }
  };

  const fetchLeads = async (loadMore = false, page = 1, searchNameOverride = null, filterTextOverride = null) => {
    setIsLoading(true);
    const pageNum = page || currentPage || 1;
    const limit = parseInt(itemsPerPage) || 20;
    const startNo = loadMore ? startIndex : (pageNum - 1) * limit;
    const endNo = startNo + limit - 1;
    const effectiveSearchName = typeof searchNameOverride === 'string' ? searchNameOverride : customSearchName;
    const effectiveFilterText = typeof filterTextOverride === 'string' ? filterTextOverride : leadFilterText;

    let finalFilter = effectiveSearchName ? '' : (effectiveFilterText || '');
    if (searchText && !effectiveSearchName) {
      const s = searchText.trim();
      const searchSql = ` (( PersonName like '${s}%') or (MobileNo1 like '${s}%') or (MobileNo2 like '${s}%') or (MobileNo3 like '${s}%') or (EmailId like '${s}%') or (EmailId1 like '${s}%') or (EmailId2 like '${s}%') )`;
      finalFilter = finalFilter ? `(${finalFilter}) AND ${searchSql}` : searchSql;
    }

    const isRedirect = location.state?.isRedirect;

    if (isRedirect) {
      const dashboardFilterText = location.state?.filterText || "";
      const dashboardNotFollowUp = location.state?.notFollowUp || 0;

      const payload = {
        "draw": pageNum,
        "order[0][column]": 0,
        "order[0][dir]": "asc",
        "start": startNo,
        "length": limit,
        "search[value]": "",
        "search[regex]": false,
        "UserId": userId || '34594',
        "ParentId": parentId || '34594',
        "SearchName": "",
        "FilterText": dashboardFilterText,
        "OrderStr": "",
        "NotFollowUp": dashboardNotFollowUp,
        "TextSearch": "",
        "StartNo": startNo + 1,
        "EndNo": endNo + 1,
        "Token": "-2295521862261168"
      };

      for (let i = 0; i <= 17; i++) {
        payload[`columns[${i}][data]`] = "";
        payload[`columns[${i}][name]`] = "";
        payload[`columns[${i}][searchable]`] = true;
        payload[`columns[${i}][orderable]`] = false;
        payload[`columns[${i}][search][value]`] = "";
        payload[`columns[${i}][search][regex]`] = false;
      }

      const formBody = new URLSearchParams();
      for (const key in payload) {
        formBody.append(key, payload[key]);
      }

      try {
        const fetchResponse = await fetch(Lead_for_Redirect, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'Accept': 'application/json'
          },
          credentials: 'omit',
          body: formBody.toString()
        });

        if (!fetchResponse.ok) throw new Error('Network response was not ok');
        const resultText = await fetchResponse.text();
        let dataResponse = null;
        try {
          const parsed = JSON.parse(resultText);
          dataResponse = parsed.d ? JSON.parse(parsed.d) : parsed;
        } catch (e) {
          console.error("Parse error:", e);
        }

        if (dataResponse) {
          const rows = Array.isArray(dataResponse?.data) ? dataResponse.data : [];
          setTotalRecord(dataResponse?.recordsFiltered || dataResponse?.recordsTotal || rows.length);
          if (loadMore) {
            setLeads(prev => [...prev, ...rows]);
          } else {
            setLeads(rows);
          }
        }
      } catch (error) {
        console.error('fetchLeads redirect error:', error);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    const details = {
      draw: pageNum,
      StartNo: startNo,
      EndNo: endNo,
      UserId: userId || '',
      SearchName: effectiveSearchName || '',
      ParentId: parentId || '',
      FilterText: finalFilter,
      OrderStr: '',
      TextSearch: '',
      NotFollowUp: location.state?.notFollowUp === 1 || activeFilters.includes('NotFollowedUpYet') ? 1 : 0
    };

    const payload = {
      Token: TokenId,
      Details: JSON.stringify(details)
    };

    try {
      const response = await serviceInstance.post('UserCRM/GetLeadDetailListNew', payload);
      if (response?.data?.Status === 1) {
        const dataResponse = response.data.Details || {};
        const rows = Array.isArray(dataResponse?.data) ? dataResponse.data : [];
        setTotalRecord(dataResponse?.recordsFiltered || dataResponse?.recordsTotal || rows.length);

        if (loadMore) {
          setLeads(prev => [...prev, ...rows]);
        } else {
          setLeads(rows);
          // Sync global total if this is a baseline fetch (no search/filter)
          if (!filterTextOverride && !searchText && !customSearchName && !leadFilterText) {
            setGlobalStats(prev => ({
              ...prev,
              total: dataResponse?.recordsTotal || prev.total
            }));
          }
        }
      }
    } catch (error) {
      console.error('fetchLeads error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSelectedLead(null);
  }, [viewMode]);

  useEffect(() => {
    const updatePos = () => {
      const sidebarEl = document.querySelector('.fixed.inset-y-0.left-0') || document.querySelector('aside');
      const sidebarWidth = sidebarEl ? sidebarEl.getBoundingClientRect().right : 64;
      setFilterBtnLeft(sidebarWidth + 8);
    };

    updatePos();

    const interval = setInterval(updatePos, 50);
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos);
    };
  }, []);

  useEffect(() => {
    if (viewMode !== 'table' && leads.length > 0 && !selectedLead) {
      setSelectedLead(leads[0]);
    }
  }, [viewMode, leads, selectedLead]);

  useEffect(() => {
    fetchLeads(false, currentPage);
    fetchStats();
    // Fetch options for filters
    (async () => {
      try {
        const payload = { Token: TokenId, Details: { ParentId: parentId || 0 } };
        const [list, src, med, cmp, fu, users] = await Promise.all([
          getCustomFieldByParentId(payload),
          bindSourceMediumCampaign({ ...payload, Details: { Type: 'SourceName', Parentid: parentId || 0 } }),
          bindSourceMediumCampaign({ ...payload, Details: { Type: 'MediumName', Parentid: parentId || 0 } }),
          bindSourceMediumCampaign({ ...payload, Details: { Type: 'CampaignName', Parentid: parentId || 0 } }),
          getFollowUpStatus(payload),
          getUserListByUserId({ Token: TokenId, Details: { UserId: userId || 0 } })
        ]);
        if (Array.isArray(list) && list.length > 0) {
          setCustomFields(list);
          setAdvancedFilters(prev => {
            const next = { ...prev };
            list.forEach(f => {
              const id = (f.FieldId ?? f.field_ID ?? f.Id ?? f.id ?? f.fieldId);
              const key = `custom_${id}`;
              if (!next[key]) next[key] = { enabled: false, operator: 'Contains', value: '' };
            });
            return next;
          });
        }
        setSourceOptions(Array.isArray(src) ? src : []);
        setMediumOptions(Array.isArray(med) ? med : []);
        setCampaignOptions(Array.isArray(cmp) ? cmp : []);
        setFollowupOptions(Array.isArray(fu) ? fu : []);
        setUserOptions(Array.isArray(users) ? users.map(u => ({ Code: String(u?.Code ?? u?.code ?? u?.UserId ?? u?.userId ?? u?.Id ?? u?.id ?? ''), Text: u?.Text ?? u?.text ?? u?.UserName ?? u?.Name ?? '' })) : []);
      } catch (err) {
        console.warn('fetch options error', err);
      }
    })();
  }, [currentPage, itemsPerPage, customSearchName, leadFilterText, debouncedSearchText]);

  // Sync activeFilters to leadFilterText
  useEffect(() => {
    let sqlParts = [];

    const yesterday = dayjs().subtract(1, 'day').format('DD-MMM-YYYY');
    const tomorrow = dayjs().add(1, 'day').format('DD-MMM-YYYY');
    const firstOfMonth = dayjs().startOf('month').format('DD-MMM-YYYY');
    const lastOfMonth = dayjs().endOf('month').format('DD-MMM-YYYY');

    if (activeFilters.includes('Today') || activeFilters.includes('CreatedToday')) {
      sqlParts.push(`CreatedOn1 >= '${yesterday}' AND CreatedOn1 <= '${tomorrow}'`);
    }

    if (activeFilters.includes('CreatedAssignedToday')) {
      sqlParts.push(`CreatedOn1 >= '${yesterday}' AND CreatedOn1 <= '${tomorrow}' AND AssignedOn >= '${yesterday}' AND AssignedOn <= '${tomorrow}'`);
    }

    if (activeFilters.includes('AssignedToday')) {
      sqlParts.push(`AssignedOn >= '${yesterday}' AND AssignedOn <= '${tomorrow}'`);
    }

    if (activeFilters.includes('AssignedTillToday')) {
      sqlParts.push(`AssignedOn <= '${tomorrow}'`);
    }

    if (activeFilters.includes('CreatedThisMonth')) {
      sqlParts.push(`CreatedOn1 >= '${firstOfMonth}' AND CreatedOn1 <= '${lastOfMonth}'`);
    }

    if (activeFilters.includes('Fresh')) {
      sqlParts.push("FollowUpStatus = 'Fresh'");
    }

    if (activeFilters.includes('Converted')) {
      sqlParts.push("FollowUpStatus = 'Converted'");
    }

    const newFilter = sqlParts.length > 0 ? `(${sqlParts.join(' OR ')})` : '';
    if (newFilter !== leadFilterText) {
      setLeadFilterText(newFilter);
      setCurrentPage(1);
    }
  }, [activeFilters]);

  const handleStatusFilterClick = (status) => {
    if (status === 'ResetAll') {
      setActiveFilters(['All']);
      setSearchText('');
      setLeadFilterText('');
      setCustomSearchName('');
      setCurrentPage(1);
      return;
    }

    setActiveFilters(prev => {
      if (status === 'All') return ['All'];
      if (prev.includes(status)) {
        const next = prev.filter(s => s !== status);
        return next.length === 0 ? ['All'] : next;
      }
      return [...prev.filter(s => s !== 'All'), status];
    });
    setCurrentPage(1);
  };


  const openNav = () => setIsFilterPanelOpen(true);
  const closeNav = () => setIsFilterPanelOpen(false);

  const updateAdvancedFilter = (key, patch) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch
      }
    }));
  };

  const sqlEscape = (value) => String(value || '').replace(/'/g, "''");

  const buildClause = (column, operator, rawValue) => {
    const value = sqlEscape(rawValue);
    if (!value) return '';

    if (operator === 'NotContains') return `${column} not like '%${value}%'`;
    if (operator === 'StartsWith') return `${column} like '${value}%'`;
    if (operator === 'EndsWith') return `${column} like '%${value}'`;
    if (operator === 'EqualTo') return `${column} = '${value}'`;
    return `${column} like '%${value}%'`;
  };

  const buildGroupClause = (columns, operator, value) => {
    const parts = columns
      .map((column) => buildClause(column, operator, value))
      .filter(Boolean);
    if (!parts.length) return '';
    return `(${parts.join(' or ')})`;
  };

  const applyAdvancedFilters = () => {
    const clauses = [];
    const summary = [];

    leadFieldDefs.forEach((f) => {
      const cfg = advancedFilters[f.key];
      if (!cfg || !cfg.enabled || !String(cfg.value || '').trim()) return;

      const val = String(cfg.value || '').trim();
      if (f.key === 'mobileNo') {
        clauses.push(buildGroupClause(['MobileNo1', 'MobileNo2', 'MobileNo3'], cfg.operator, val));
        summary.push(`${f.label}: ${val}`);
        return;
      }
      if (f.key === 'emailId') {
        clauses.push(buildGroupClause(['EmailId', 'EmailId1', 'EmailId2'], cfg.operator, val));
        summary.push(`${f.label}: ${val}`);
        return;
      }

      const col = f.db || f.label.replace(/\s+/g, '');
      clauses.push(buildClause(col, cfg.operator, val));
      summary.push(`${f.label}: ${val}`);
    });

    if (Array.isArray(customFields) && customFields.length > 0) {
      customFields.forEach((f) => {
        const id = (f.FieldId ?? f.field_ID ?? f.Id ?? f.id ?? f.fieldId);
        const key = `custom_${id}`;
        const cfg = advancedFilters[key];
        if (cfg && cfg.enabled && String(cfg.value || '').trim()) {
          const col = String(f.FieldName || f.FieldNameText || f.FieldNameLabel || f.FieldLabel || f.Name || f.Field || '').trim();
          if (col) {
            clauses.push(buildClause(col, cfg.operator, cfg.value));
            summary.push(`${col}: ${cfg.value.trim()}`);
          }
        }
      });
    }

    const finalClauses = clauses.filter(Boolean);
    if (!finalClauses.length) {
      Swal.fire('Info', 'Please select at least one filter condition.', 'info');
      return;
    }

    const sqlWhere = `(${finalClauses.join(' and ')})`;

    setCustomSearchName('');
    setSearchText('');
    setLeadFilterText(sqlWhere);
    setCurrentPage(1);
    closeNav();
  };

  const clearAdvancedFilters = () => {
    const base = makeInitialFilters();
    if (Array.isArray(customFields) && customFields.length > 0) {
      customFields.forEach(f => {
        const id = (f.FieldId ?? f.field_ID ?? f.Id ?? f.id ?? f.fieldId);
        const key = `custom_${id}`;
        base[key] = { enabled: false, operator: 'Contains', value: '' };
      });
    }
    setAdvancedFilters(base);
    setLeadFilterText('');
    setSearchText('');
    setCustomSearchName('');
    setCurrentPage(1);
    closeNav();
  };

  const filterOperators = ['Contains', 'NotContains', 'StartsWith', 'EndsWith', 'EqualTo'];

  const handleMassAction = (action) => {
    switch (action) {
      case 'followup': setIsMassAddFollowupModal(true); break;
      case 'sms': setIsMassSendSMS(true); break;
      case 'mail': setIsMassSendMail(true); break;
      case 'voice': setIsMassSendVoiceBroadCast(true); break;
      case 'mass_update': setIsMassUpdateLead(true); break;
      case 'addtask': setIsMassAddTask(true); break;
      case 'addappointment': setIsMassAddAppointment(true); break;
      case 'marketing_sequence': setIsMassMarketingSequence(true); break;
      case 'international_sms': setIsSendInternationalSMS(true); break;
      case 'add_tag': setIsMassAddTag(true); setIsAddTag(true); break;
      case 'remove_tag': setIsMassAddTag(true); setIsAddTag(false); break;
      case 'add_call_list': setIsMassAddCallList(true); break;
      case 'delete': setShowMassDeleteConfirm(true); break;
      default: console.warn('Unknown mass action:', action);
    }
  };

  const handleMassActionSubmit = (handler, mode = 'direct') => {
    if (typeof handler === 'function') {
      if (mode === 'double') {
        handler()();
      } else {
        handler();
      }
    }
  };

  const handleDeleteLead = async (lead) => {
    const leadId = lead.LeadId || lead.ID || lead.Id;
    Swal.fire({
      title: 'Checking permissions...',
      didOpen: () => { Swal.showLoading(); },
      allowOutsideClick: false
    });

    let canDeleteThis = true;
    try {
      const result = await checkLeadPermission({
        leadId,
        logUserId: userId,
        Token: TokenId
      });
      Swal.close();
      const permRow = result?.Details && Array.isArray(result.Details) && result.Details.length > 0
        ? result.Details[0]
        : (result?.Details || null);
      canDeleteThis = permRow ? permRow.Delete !== false : (permissions ? permissions.Delete !== false : true);
    } catch (e) {
      Swal.close();
    }

    if (!canDeleteThis) {
      Swal.fire('Access Denied', 'You do not have permission to delete this lead.', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const payload = {
          Token: TokenId,
          Details: JSON.stringify({ LeadId: lead.ID || lead.LeadId || lead.Id, UserId: userId })
        };
        const response = await serviceInstance.post('UserCRM/DeleteLeadDetail', payload);
        if (response.data.Status === 1) {
          Swal.fire('Deleted!', 'Lead has been deleted.', 'success');
          fetchLeads();
        } else {
          Swal.fire('Error', response.data.Message || 'Failed to delete lead', 'error');
        }
      } catch (error) {
        Swal.fire('Error', 'An error occurred', 'error');
      }
    }
  };

  if (permissions && !canView) {
    return <PermissionDeniedComponent />;
  }

  return (
    <div className="flex flex-col bg-gray-50 relative" style={{ height: '100%', flex: 1, overflow: 'hidden' }}>

      {!isFilterPanelOpen && (
        <button
          type="button"
          onClick={openNav}
          className="fixed top-1/2 -translate-y-1/2 w-10 h-10 bg-white border border-gray-300 rounded shadow-md hover:bg-gray-50 flex items-center justify-center"
          style={{ left: `${filterBtnLeft}px`, zIndex: 40 }}
          title="Filter here"
        >
          <Filter className="w-5 h-5 text-green-600" />
        </button>
      )}

      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={closeNav}
        leadFieldDefs={leadFieldDefs}
        advancedFilters={advancedFilters}
        updateAdvancedFilter={updateAdvancedFilter}
        filterOperators={filterOperators}
        sourceOptions={sourceOptions}
        mediumOptions={mediumOptions}
        campaignOptions={campaignOptions}
        followupOptions={followupOptions}
        userOptions={userOptions}
        customFields={customFields}
        applyAdvancedFilters={applyAdvancedFilters}
        clearAdvancedFilters={clearAdvancedFilters}
      />
      <LeadHeader
        searchQuery={searchText}
        setSearchQuery={setSearchText}
        totalLeads={totalRecord}
        viewMode={viewMode}
        setViewMode={setViewMode}
        selectedCount={Object.keys(rowSelection).length}
        onClearSelection={() => {
          setRowSelection({});
          setIsAllSelectedAcrossPages(false);
        }}
        onMassAction={handleMassAction}
        isAllSelectedAcrossPages={isAllSelectedAcrossPages}
        setIsAllSelectedAcrossPages={setIsAllSelectedAcrossPages}
        advanceFilterFields={leadFieldDefs}
        onAdvanceFilterSubmit={(filters) => {
          console.log("Advanced Filters:", filters);
        }}
        onCustomSearchSubmit={(name) => {
          setCustomSearchName(name);
          setLeadFilterText('');
          setSearchText('');
          fetchLeads(false, 1, name, '');
        }}
        onCustomSearchClear={() => {
          setCustomSearchName('');
          if (searchParams.has('searchName')) {
            searchParams.delete('searchName');
            setSearchParams(searchParams);
          }
          fetchLeads(false, 1, '', '');
        }}
        customSearchFetcher={() => getLeadCustomSearch({ UserId: userId })}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        onExportLeads={handleExportLeadList}
        onImportLeads={() => setIsImportDataModal(true)}
        onAddLead={() => setShowAddLeadModal(true)}
        onCustomFieldsClick={() => setShowCustomFields(true)}
        permissions={permissions}
      />

      {viewMode !== 'table' && (
        <div className="px-4 py-2 border-b border-gray-200 bg-white">
          <PremiumModuleCards
            customCards={[
              { title: globalStats?.total || 0, subtitle: 'Total Leads', icon: 'ri:user-star-line', color: 'primary', key: 'All' },
              { title: globalStats?.today || 0, subtitle: "Today's Created Leads", icon: 'ri:calendar-event-line', color: 'success', key: 'Today' }
            ]}
            onStatusClick={handleStatusFilterClick}
            activeFilters={activeFilters}
          />
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden" style={{ minHeight: 0 }}>

        <div className="flex-1 flex overflow-hidden" style={{ minHeight: 0 }}>
          {viewMode === 'table' ? (
            <LeadListWrapper
              data={leads}
              stats={globalStats}
              loading={isLoading}
              totalRecords={totalRecord}
              currentPage={currentPage}
              itemsPerPage={parseInt(itemsPerPage)}
              onPageChange={setCurrentPage}
              onStatusChange={handleStatusFilterClick}
              rowSelection={rowSelection}
              onManualRowSelectionChange={setRowSelection}
              onSelectAll={() => {
                const newSel = {};
                leads.forEach(l => {
                  const id = l.ID || l.LeadId || l.Id;
                  if (id !== undefined && id !== null) {
                    newSel[String(id)] = true;
                  }
                });
                setRowSelection(newSel);
              }}
              activeFilters={activeFilters}
              searchTerm={searchText}
              onViewLead={(lead) => {
                setSelectedLead(lead);
              }}
              onEditLead={(lead) => {
                setSelectedLeadForEdit(lead);
                setShowEditLeadModal(true);
              }}
              onDeleteLead={handleDeleteLead}
              onRowAction={(lead, action) => {
                console.log("Row Action:", action, lead);
              }}
              onScheduleCall={(lead) => {
                setScheduleLead(lead);
                setShowScheduleModal(true);
              }}
              onInitiateCall={handleInitiateCall}
            />
          ) : (
            <div className="flex-1 flex overflow-hidden bg-white">
              <div className="w-[380px] border-r border-gray-200 flex flex-col min-h-0">
                <LeadList
                  leads={leads}
                  selectedLead={selectedLead?.ID || selectedLead?.LeadId || selectedLead?.Id}
                  onSelectLead={setSelectedLead}
                  searchText={searchText}
                  onSearchChange={setSearchText}
                  onSearch={() => fetchLeads()}
                  activeCustomSearchName={customSearchName}
                  onCustomSearchApply={(name) => {
                    setCustomSearchName(name);
                    setLeadFilterText('');
                    setSearchText('');
                    fetchLeads(false, 1, name, '');
                  }}
                  isLoading={isLoading}
                  hasMore={leads.length < totalRecord}
                  onLoadMore={() => fetchLeads(true)}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  totalRecord={totalRecord}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={setItemsPerPage}
                />
              </div>
              <div className="flex-1 overflow-y-auto bg-gray-50">
                {selectedLead ? (
                  <UniversalDetailContent
                    id={selectedLead.ID || selectedLead.LeadId || selectedLead.Id}
                    type="lead"
                    detailData={selectedLead}
                    userId={userId}
                    onViewLead={(id) => {
                       const lead = leads.find(l => (l.ID || l.LeadId || l.Id) === id);
                       if (lead) setSelectedLead(lead);
                    }}
                  />
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8">
                    <img src={nodata} alt="No selection" className="w-64 h-64 object-contain opacity-50 mb-4" />
                    <p className="text-lg font-medium">Select a lead to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Drawer for table mode */}
      <Drawer
        isOpen={viewMode === 'table' && !!selectedLead}
        onClose={() => setSelectedLead(null)}
        title="Lead Details"
        size="wide"
      >
        {selectedLead && (
          <LeadDetails
            lead={selectedLead}
            onBack={() => setSelectedLead(null)}
            leadPermissions={leadPermissions}
            maskedFields={maskedFields}
          />
        )}
      </Drawer>

      {/* Mass Operation Modals */}
      <PopUpModal
        isOpen={isMassAddFollowupModal}
        onClose={() => setIsMassAddFollowupModal(false)}
        title="Mass Add Followup"
        size="lg"
        footer={null}
      >
        <AddFollowupMassOperation
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => setIsMassAddFollowupModal(false)}
          onSave={() => {
            setIsMassAddFollowupModal(false);
            setRowSelection({});
            fetchLeads();
            fetchStats();
          }}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassSendSMS}
        onClose={() => {
          setIsMassSendSMS(false);
          setMassSmsSubmitHandler(null);
        }}
        title="Send SMS"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassSendSMS(false); setMassSmsSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massSmsSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <SendSMS
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsMassSendSMS(false); setMassSmsSubmitHandler(null); }}
          onSuccess={() => { setIsMassSendSMS(false); setMassSmsSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassSmsSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassSendMail}
        onClose={() => {
          setIsMassSendMail(false);
          setMassMailSubmitHandler(null);
        }}
        title="Send Mail"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassSendMail(false); setMassMailSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massMailSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <SendMail
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsMassSendMail(false); setMassMailSubmitHandler(null); }}
          onSuccess={() => { setIsMassSendMail(false); setMassMailSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassMailSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassSendVoiceBroadCast}
        onClose={() => {
          setIsMassSendVoiceBroadCast(false);
          setMassVoiceSubmitHandler(null);
        }}
        title="Send Voice BroadCast"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassSendVoiceBroadCast(false); setMassVoiceSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massVoiceSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <VoiceBroadCast
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsMassSendVoiceBroadCast(false); setMassVoiceSubmitHandler(null); }}
          onSuccess={() => { setIsMassSendVoiceBroadCast(false); setMassVoiceSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassVoiceSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassUpdateLead}
        onClose={() => {
          setIsMassUpdateLead(false);
          setMassUpdateSubmitHandler(null);
        }}
        title="Mass Update Lead"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassUpdateLead(false); setMassUpdateSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massUpdateSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <UpdateForm
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsMassUpdateLead(false); setMassUpdateSubmitHandler(null); }}
          onUpdate={() => { setIsMassUpdateLead(false); setMassUpdateSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassUpdateSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassAddTask}
        onClose={() => {
          setIsMassAddTask(false);
          setMassTaskSubmitHandler(null);
        }}
        title="Mass Add Task"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassAddTask(false); setMassTaskSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massTaskSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <AddTaskForm
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsMassAddTask(false); setMassTaskSubmitHandler(null); }}
          onSave={() => { setIsMassAddTask(false); setMassTaskSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassTaskSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassAddAppointment}
        onClose={() => {
          setIsMassAddAppointment(false);
          setMassAppointmentSubmitHandler(null);
        }}
        title="Mass Add Appointment"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassAddAppointment(false); setMassAppointmentSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massAppointmentSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <AddAppointmentForm
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsMassAddAppointment(false); setMassAppointmentSubmitHandler(null); }}
          onSave={() => { setIsMassAddAppointment(false); setMassAppointmentSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassAppointmentSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassMarketingSequence}
        onClose={() => {
          setIsMassMarketingSequence(false);
          setMassSequenceSubmitHandler(null);
        }}
        title="Mass Marketing Sequence"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassMarketingSequence(false); setMassSequenceSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massSequenceSubmitHandler, 'double')}>Save</Button>
          </div>
        }
      >
        <MarkettingSequence
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsMassMarketingSequence(false); setMassSequenceSubmitHandler(null); }}
          onSave={() => { setIsMassMarketingSequence(false); setMassSequenceSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassSequenceSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isSendInternationalSMS}
        onClose={() => {
          setIsSendInternationalSMS(false);
          setMassIntlSmsSubmitHandler(null);
        }}
        title="Send International SMS"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsSendInternationalSMS(false); setMassIntlSmsSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massIntlSmsSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <SendInternationalSMS
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsSendInternationalSMS(false); setMassIntlSmsSubmitHandler(null); }}
          onSuccess={() => { setIsSendInternationalSMS(false); setMassIntlSmsSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassIntlSmsSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassAddTag}
        onClose={() => {
          setIsMassAddTag(false);
          setMassTagSubmitHandler(null);
        }}
        title={isAddTag ? "Mass Add Tag" : "Mass Remove Tag"}
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassAddTag(false); setMassTagSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massTagSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <AddRemoveTag
          mode={isAddTag ? 'add' : 'remove'}
          selectedCount={Object.keys(rowSelection).length}
          selectedLeadIds={Object.keys(rowSelection)}
          onClose={() => { setIsMassAddTag(false); setMassTagSubmitHandler(null); }}
          onSave={() => { setIsMassAddTag(false); setMassTagSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassTagSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isMassAddCallList}
        onClose={() => {
          setIsMassAddCallList(false);
          setMassCallListSubmitHandler(null);
        }}
        title="Mass Add Call List"
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => { setIsMassAddCallList(false); setMassCallListSubmitHandler(null); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleMassActionSubmit(massCallListSubmitHandler)}>Save</Button>
          </div>
        }
      >
        <AddCallList
          selectedCount={Object.keys(rowSelection).length}
          onClose={() => { setIsMassAddCallList(false); setMassCallListSubmitHandler(null); }}
          onSave={() => { setIsMassAddCallList(false); setMassCallListSubmitHandler(null); setRowSelection({}); fetchLeads(); fetchStats(); }}
          registerSubmitHandler={setMassCallListSubmitHandler}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={showMassDeleteConfirm}
        onClose={() => setShowMassDeleteConfirm(false)}
        title="Mass Delete Leads"
        size="sm"
      >
        <div className="p-4">
          <p className="text-gray-700 mb-4">
            You are about to perform a mass delete action on <strong>{Object.keys(rowSelection).length}</strong> selected record(s).
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowMassDeleteConfirm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                // Simplified mass delete - loop through or call bulk API if available
                Swal.fire({
                  title: 'Deleting...',
                  didOpen: () => { Swal.showLoading(); }
                });

                try {
                  const ids = Object.keys(rowSelection);
                  for (const id of ids) {
                    const payload = {
                      Token: TokenId,
                      Details: JSON.stringify({ LeadId: id, UserId: userId })
                    };
                    await serviceInstance.post('UserCRM/DeleteLeadDetail', payload);
                  }
                  Swal.fire('Deleted!', 'Selected leads have been deleted.', 'success');
                  setShowMassDeleteConfirm(false);
                  setRowSelection({});
                  fetchLeads();
                  fetchStats();
                } catch (error) {
                  Swal.fire('Error', 'Failed to delete some leads.', 'error');
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Confirm Delete
            </button>
          </div>
        </div>
      </PopUpModal>

      {/* Modals */}
      {/* Add Lead Drawer Overlay */}
      {showAddLeadModal && (
        <div className="add-lead-drawer-overlay" onClick={() => setShowAddLeadModal(false)}>
          <div className="add-lead-drawer" onClick={(e) => e.stopPropagation()}>
            <AddLeadForm
              onSaved={() => {
                setShowAddLeadModal(false);
                fetchLeads();
                fetchStats();
              }}
              onClose={() => setShowAddLeadModal(false)}
            />
          </div>
        </div>
      )}

      <PopUpModal
        isOpen={showEditLeadModal}
        onClose={() => setShowEditLeadModal(false)}
        title="Edit Lead"
        size="xl"
      >
        <EditLeadForm
          lead={selectedLeadForEdit}
          onSubmit={() => { setShowEditLeadModal(false); fetchLeads(); }}
          onClose={() => setShowEditLeadModal(false)}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title="Schedule Call"
      >
        <ScheduleCallForm
          lead={scheduleLead}
          onCancel={() => setShowScheduleModal(false)}
          onSave={() => setShowScheduleModal(false)}
        />
      </PopUpModal>

      <PopUpModal
        isOpen={isImportDataModal}
        onClose={() => setIsImportDataModal(false)}
        title="Import Data"
        size="lg"
      >
        <ImportData
          onClose={() => setIsImportDataModal(false)}
          onSuccess={() => {
            setIsImportDataModal(false);
            fetchLeads();
            fetchStats();
          }}
        />
      </PopUpModal>

      {showCustomFields && (
        <div style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'white', display: 'flex', flexDirection: 'column' }}>
          <LeadCustomField onClose={() => setShowCustomFields(false)} />
        </div>
      )}

      {showCallWidget && callWidgetData && createPortal(
        <CallWidget
          show={showCallWidget}
          onClose={() => setShowCallWidget(false)}
          toMobileNo={callWidgetData.toMobileNo}
          toEmail={callWidgetData.toEmail}
          extraContactInfo={callWidgetData.extraContactInfo}
          entityId={callWidgetData.entityId}
          entityName={callWidgetData.entityName}
          personName={callWidgetData.personName}
          triggerRect={callWidgetData.triggerRect}
        />,
        document.body
      )}
    </div>
  );
};

export default Leads;

